import { Box, Toolbar } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../components/commons/header/Header';
import { useSidebar } from '../stores/sidebar/SidebarContext';
import { SidebarProvider } from '../stores/sidebar/SidebarProvider';
import { NavigationSideMenu } from '../components/commons/navigation/NavigationSideMenu';
import type { MenuKeyLabel } from '../components/commons/navigation/NavigationSideMenu.types';
import { useRef, useState } from 'react';
import theme from '../theme/theme';
import { ErrorBoundary } from '../components/error/ErrorBoundary';
import { ErrorFallback } from '../components/error/ErrorFallback';

function DashboardContent() {
  const { isOpen, toggle } = useSidebar();
  const [expandedKeys, setExpandedKeys] = useState<MenuKeyLabel[]>(['myNGO']);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const handleCloseMobile = () => {
    toggle();
    requestAnimationFrame(() => {
      menuButtonRef.current?.focus();
    });
  };

  return (
    // Fill the viewport and clip it, so the scroll happens inside `main` (below)
    // rather than on the window. A window-level scroll (with the `overflow:hidden`
    // content column) traps `position: sticky` — making `main` the scroll
    // container lets sticky headers work.
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <NavigationSideMenu
        drawerOpen={isOpen}
        onCloseMobile={handleCloseMobile}
        expandedKeys={expandedKeys}
        onToggleExpand={key =>
          setExpandedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
          )
        }
      />

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Header ref={menuButtonRef} onMenuToggle={toggle} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // `main` is the app's scroll container (see root note). `minHeight:0`
            // lets it shrink inside the flex column so `overflowY:auto` engages.
            minHeight: 0,
            overflowY: 'auto',
            bgcolor: theme.palette.common.white,
            transition: `margin 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          {/* Spacer offsets content below the fixed AppBar; a sticky header uses
              top: theme.toolbar.height to pin just under it. */}
          <Toolbar variant="dense" />
          <ErrorBoundary
            resetKeys={[location.pathname]}
            fallback={({ error, resetError }) => (
              <ErrorFallback error={error} resetError={resetError} />
            )}
          >
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}

export function DashboardLayout() {
  return (
    <SidebarProvider>
      <DashboardContent />
    </SidebarProvider>
  );
}
